// Department Balances (zeenat_pret only) -- where every piece of one Article's
// job card is right now, department by department.
//
// For each department in the flow (Cutting -> ... -> Dispatch) it shows the
// three balances agreed with the client:
//   To be produced     received from the previous department, not worked on
//   To be reworked     sent back to this department to be fixed
//   Produced, not sent finished here, not yet handed on
// plus "Sent, not received yet" -- transfers still in progress. That last one
// is not a balance; it is shown so no piece is unaccounted for.
//
// Any balance can be clicked for its "Why?" -- the same popup as the Transfer
// Issue form's Available row, built on the same rows the save check uses
// (node_backendAS/helper/deptBalanceHelper.js explainBalance).
//
// Balances are dated: the grid is a snapshot AS OF a date (default today) --
// the balances at the end of that day. Future dates cannot be picked.
//
// Balances are per PART of the job card's tech pack (14-Oct-26, see PARTS in
// node_backendAS/models/deptBalanceModel.js): the grid shows one part at a
// time, picked under Part (the first part by default). "Sent, not received
// yet" is per garment -- transfers still move whole garments -- so it is the
// same for every part.
//
// Read-only. Rights: View on /deptBalanceReport.
import React, { useEffect, useState } from "react";
import axios from "axios";
import Select from "react-select";
import Swal from "sweetalert2";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Button, Spinner } from "reactstrap";
import { baseurl } from "../../../baseurl";
import { checkPermission } from "../../../Helper";
import "../ShortCutComponents/overviewScreen.css";
import "./deptTransfer.css";
import BalanceWhyModal from "./BalanceWhyModal";

const token = localStorage.getItem("token");
const username = localStorage.getItem("username");
const companyId = localStorage.getItem("companyId");

const PAGE_PATH = "/deptBalanceReport";
const headers = { headers: { authorization: token } };

const SIZES = ["XS", "S", "M", "L", "XL"];
const SIZE_TABS = ["All", ...SIZES];

const qty = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 2 });

// The clickable balances, in column order. resultLabel is the popup's last line.
const COLUMNS = [
  {
    key: "toProduce",
    bucket: "to_produce",
    label: "To be produced",
    hint: "Received from the previous department, not worked on yet",
  },
  {
    key: "toRework",
    bucket: "to_rework",
    label: "To be reworked",
    hint: "Sent back to this department to be fixed",
  },
  {
    key: "produced",
    bucket: "produced",
    label: "Produced, not sent",
    hint: "Finished here, not yet handed to the next department",
  },
];

export default function DeptBalanceReport() {
  const [articles, setArticles] = useState([]);
  const [article, setArticle] = useState(null);
  const [size, setSize] = useState("All");
  const [asOf, setAsOf] = useState(new Date());
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [allowed, setAllowed] = useState(false);

  // "Why?" popup.
  const [why, setWhy] = useState(null); // { dept, column }
  const [whyDetail, setWhyDetail] = useState(null);
  const [whyLoading, setWhyLoading] = useState(false);

  useEffect(() => {
    (async () => {
      const denied = await checkPermission(PAGE_PATH, 0);
      if (denied) return;
      setAllowed(true);
      axios
        .post(`${baseurl}/deptTransfer/getArticles`, { companyId, username }, headers)
        .then((res) => setArticles(res.data.articles || []))
        .catch(() => setArticles([]));
    })();
  }, []);

  const asOfKey = (d) => moment(d || new Date()).format("YYYY-MM-DD");

  // partId: null = the server's default (the first tech-pack part).
  const load = async (a, d = asOf, partId = null) => {
    setArticle(a);
    setData(null);
    if (!a) return;
    setBusy(true);
    try {
      const res = await axios.post(
        `${baseurl}/deptTransfer/getJobCardBalances`,
        { articleId: a.id, asOf: asOfKey(d), partId, companyId, username },
        headers,
      );
      setData(res.data);
    } catch (error) {
      Swal.fire(
        "Error",
        (error.response && error.response.data && error.response.data.message) ||
          "Could not load the balances.",
        "error",
      );
    } finally {
      setBusy(false);
    }
  };

  const openWhy = async (dept, column) => {
    setWhy({ dept, column });
    setWhyDetail(null);
    setWhyLoading(true);
    try {
      const res = await axios.post(
        `${baseurl}/deptTransfer/getBalanceDetail`,
        {
          articleId: article.id,
          deptId: dept.id,
          bucket: column.bucket,
          partId: data ? data.partId : null,
          // The popup explains the same date the grid shows.
          date: data ? data.asOf : asOfKey(asOf),
          companyId,
          username,
        },
        headers,
      );
      setWhyDetail(res.data);
    } catch (error) {
      setWhy(null);
      Swal.fire("Error", "Could not read the balance.", "error");
    } finally {
      setWhyLoading(false);
    }
  };

  // The number for the chosen size (or the total), plus the per-size line for
  // the hover when showing All.
  const valueOf = (cell) => (size === "All" ? cell.total : cell.sizes[size]);
  const perSize = (cell) => SIZES.map((k) => `${k} ${qty(cell.sizes[k])}`).join("  ·  ");

  const renderBalance = (dept, column) => {
    const cell = dept[column.key];
    // Cutting starts the flow: nothing is ever received there.
    if (column.key === "toProduce" && dept.isFirst) {
      return (
        <span className="dbr-dash" title={`${dept.label} starts the flow, so nothing is received here.`}>
          –
        </span>
      );
    }
    const v = Number(valueOf(cell) || 0);
    return (
      <button
        type="button"
        className={"dbr-cell" + (v < -0.005 ? " dbr-neg" : Math.abs(v) < 0.005 ? " dbr-zero" : "")}
        title={(size === "All" ? perSize(cell) + "\n" : "") + "Click to see how this is worked out"}
        onClick={() => openWhy(dept, column)}
      >
        {qty(v)}
      </button>
    );
  };

  const renderTransit = (dept) => {
    const v = Number(valueOf(dept.inTransit) || 0);
    return (
      <span
        className={"dbr-transit" + (Math.abs(v) < 0.005 ? " dbr-zero" : "")}
        title={size === "All" ? perSize(dept.inTransit) : ""}
      >
        {qty(v)}
      </span>
    );
  };

  return (
    <div className="animated fadeIn ocv-page">
      <BalanceWhyModal
        isOpen={!!why}
        onClose={() => setWhy(null)}
        loading={whyLoading}
        detail={whyDetail}
        mode="snapshot"
        title={why ? `${why.dept.label}: ${why.column.label}` : ""}
        resultLabel={why ? why.column.label : ""}
      />
      <div className="ocv-card">
        <div className="ocv-header">
          <div>
            <h4 className="ocv-title">Department Balances</h4>
            <div className="ocv-subtitle">
              Where every piece of a job card is, department by department
            </div>
          </div>
        </div>

        <div className="ocv-filters ocv-filters-inline">
          <div className="ocv-filter-field dbr-article">
            <label>Article</label>
            <Select
              value={article}
              options={articles}
              onChange={(a) => load(a || null)}
              isClearable
              isDisabled={!allowed || busy}
              placeholder="Pick an article to see its balances"
            />
          </div>
          <div className="ocv-filter-field dpf-field">
            <label>As of</label>
            <DatePicker
              selected={asOf}
              onChange={(d) => {
                const next = d || new Date();
                setAsOf(next);
                if (article) load(article, next, data ? data.partId : null);
              }}
              dateFormat="dd-MMM-yyyy"
              maxDate={new Date()}
              disabled={!allowed || busy}
            />
          </div>
          <div className="ocv-filter-field dbr-article">
            <label>Part</label>
            <Select
              value={
                data && data.partId ? { id: data.partId, value: data.partId, label: data.partLabel } : null
              }
              options={(data && data.parts) || []}
              onChange={(p) => p && load(article, asOf, p.id)}
              isDisabled={!allowed || busy || !data}
              placeholder={article ? "No parts in the tech pack" : "Article first"}
            />
          </div>
          <div className="ocv-filter-field">
            <label>Size</label>
            <div className="btn-group btn-group-sm dbr-sizes" role="group">
              {SIZE_TABS.map((k) => (
                <Button
                  key={k}
                  color={size === k ? "primary" : "light"}
                  onClick={() => setSize(k)}
                  disabled={busy}
                >
                  {k}
                </Button>
              ))}
            </div>
          </div>
        </div>

        {busy && (
          <div className="ocv-loading">
            <Spinner size="sm" color="primary" className="mr-2" />
            Loading balances…
          </div>
        )}

        {!busy && !data && (
          <div className="dbr-empty">
            Pick an Article above. Its job card's pieces are shown for every department in the flow.
          </div>
        )}

        {!busy && data && (
          <>
            <div className="dbr-plan">
              <div>
                <span className="dbr-plan-label">Job Card</span>
                <strong>{data.jcLabel}</strong>
              </div>
              <div>
                <span className="dbr-plan-label">Part</span>
                <strong>{data.partLabel || "—"}</strong>
              </div>
              <div>
                <span className="dbr-plan-label">Planned</span>
                {SIZES.map((k) => (
                  <span key={k} className={"dbr-plan-size" + (size === k ? " dbr-plan-on" : "")}>
                    {k} <strong>{qty(data.plan.sizes[k])}</strong>
                  </span>
                ))}
                <span className={"dbr-plan-size" + (size === "All" ? " dbr-plan-on" : "")}>
                  Total <strong>{qty(data.plan.total)}</strong>
                </span>
              </div>
            </div>

            {!data.partId && (
              <div className="dbr-note">
                This Article's tech pack has no parts, so it has no balances: production is entered
                per part, and a transfer counts as every part of the tech pack.
              </div>
            )}

            {!data.receiptsLive && (
              <div className="dbr-note">
                Receiving is not live yet, so nothing has been received in any department.
                Until it is, <em>To be produced</em> can only go down and may show below zero.
              </div>
            )}

            <div className="dbr-table-wrap">
              <table className="dbr-table">
                <thead>
                  <tr>
                    <th>Department</th>
                    {COLUMNS.map((c) => (
                      <th key={c.key} title={c.hint}>
                        {c.label}
                      </th>
                    ))}
                    <th className="dbr-th-transit" title="Transfers this department sent that the other side has not received yet. Not a balance.">
                      Sent, not received yet
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.departments.map((d) => (
                    <tr key={d.id}>
                      <td className="dbr-dept">{d.label}</td>
                      {COLUMNS.map((c) => (
                        <td key={c.key}>{renderBalance(d, c)}</td>
                      ))}
                      <td className="dbr-td-transit">{renderTransit(d)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="dbr-foot">
              Balances of {data.partLabel || "the part"} at the end of{" "}
              {moment(data.asOf, "YYYY-MM-DD").format("DD-MMM-YYYY")}, showing{" "}
              {size === "All" ? "all sizes (hover a number for each size)" : `size ${size}`}.
              Click any balance to see the documents behind it.
            </div>
          </>
        )}
      </div>
    </div>
  );
}
